import { supabase } from '@/lib/supabase';

/**
 * Attaches the quoted post (with author profile) and the direct reply count to
 * each post. Done with separate lookups so it never depends on PostgREST
 * relationship detection (which is unreliable for self references).
 */
async function attachPostMeta(posts: any[] | null) {
  const rows = posts || [];
  if (rows.length === 0) return rows;

  const quotedIds = [...new Set(rows.map((p: any) => p.quoted_post_id).filter(Boolean))];
  const rowIds = rows.map((p: any) => p.id).filter(Boolean);

  const [quotedPosts, replyRows] = await Promise.all([
    quotedIds.length
      ? (supabase as any).from('posts').select('*, profiles (*), post_media (*)').in('id', quotedIds)
      : Promise.resolve({ data: [] }),
    rowIds.length
      ? (supabase as any).from('posts').select('reply_to_id').in('reply_to_id', rowIds)
      : Promise.resolve({ data: [] }),
  ]);

  const postMap = new Map((quotedPosts.data || []).map((p: any) => [p.id, p]));
  const countMap = new Map<string, number>();
  (replyRows.data || []).forEach((r: any) => {
    countMap.set(r.reply_to_id, (countMap.get(r.reply_to_id) || 0) + 1);
  });

  return rows.map((p: any) => ({
    ...p,
    comments_count: countMap.get(p.id) || 0,
    quoted_post: p.quoted_post_id ? postMap.get(p.quoted_post_id) || null : (p.quoted_post || null),
  }));
}

export const authService = {
  signUp: async (email: string, password: string, metadata: { username: string; display_name: string }) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: metadata },
    });
    return { data, error };
  },
  signIn: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { data, error };
  },
  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  },
  resetPassword: async (email: string) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${import.meta.env.VITE_APP_URL || window.location.origin}/reset-password`,
    });
    return { data, error };
  },
  updatePassword: async (password: string) => {
    const { data, error } = await supabase.auth.updateUser({ password });
    return { data, error };
  },
  getSession: async () => {
    const { data } = await supabase.auth.getSession();
    return data.session;
  },
  onAuthStateChange: (callback: (event: string, session: any) => void) => {
    return supabase.auth.onAuthStateChange(callback);
  },
  getProfile: async (userId: string) => {
    const { data, error } = await (supabase as any)
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    return { data, error };
  },
};

export const profileService = {
  getProfile: async (userId: string) => {
    const { data, error } = await (supabase as any)
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    return { data, error };
  },
  getProfileByUsername: async (username: string) => {
    const { data, error } = await (supabase as any)
      .from('profiles')
      .select('*')
      .eq('username', username)
      .single();
    return { data, error };
  },
  updateProfile: async (userId: string, updates: any) => {
    const { data, error } = await (supabase as any)
      .from('profiles')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', userId)
      .select()
      .single();
    return { data, error };
  },
  uploadAvatar: async (userId: string, file: File) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}/${crypto.randomUUID()}.${fileExt}`;
    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(fileName, file, { cacheControl: '3600', upsert: false });
    if (uploadError) return { data: null, error: uploadError };
    const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(fileName);
    return { data: urlData.publicUrl, error: null };
  },
  uploadCover: async (userId: string, file: File) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}/${crypto.randomUUID()}.${fileExt}`;
    const { error: uploadError } = await supabase.storage
      .from('covers')
      .upload(fileName, file, { cacheControl: '3600', upsert: false });
    if (uploadError) return { data: null, error: uploadError };
    const { data: urlData } = supabase.storage.from('covers').getPublicUrl(fileName);
    return { data: urlData.publicUrl, error: null };
  },
  updateOnlineStatus: async (onlineAt?: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { error: new Error('Not authenticated') };
    const { error } = await (supabase as any)
      .from('profiles')
      .update({ online_at: onlineAt ?? new Date().toISOString() })
      .eq('id', user.id);
    return { error };
  },
};

export const postService = {
  createPost: async (
    content: string | null,
    mediaFiles: File[],
    options?: {
      quotedPostId?: string | null;
      replyToId?: string | null;
      rootPostId?: string | null;
    },
  ) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: null, error: new Error('Not authenticated') };

    const insertPayload: Record<string, any> = { user_id: user.id, content };
    if (options?.quotedPostId) insertPayload.quoted_post_id = options.quotedPostId;
    if (options?.replyToId) insertPayload.reply_to_id = options.replyToId;
    if (options?.rootPostId) insertPayload.root_post_id = options.rootPostId;

    const { data: post, error: postError } = await (supabase as any)
      .from('posts')
      .insert(insertPayload)
      .select()
      .single();
    if (postError) return { data: null, error: postError };

    if (mediaFiles.length > 0) {
      const mediaInserts = await Promise.all(
        mediaFiles.map(async (file, index) => {
          const fileExt = file.name.split('.').pop();
          const fileName = `posts/${post.id}/${index}-${crypto.randomUUID()}.${fileExt}`;
          const { error } = await supabase.storage
            .from('post-media')
            .upload(fileName, file, { cacheControl: '3600', upsert: false });
          if (error) return null;
          const { data: urlData } = supabase.storage.from('post-media').getPublicUrl(fileName);
          return { post_id: post.id, url: urlData.publicUrl, type: 'image', order_index: index };
        })
      );

      const validMedia = mediaInserts.filter(Boolean);
      if (validMedia.length > 0) {
        await (supabase as any).from('post_media').insert(validMedia);
      }
    }

    return { data: post, error: null };
  },
  getPosts: async (filter: 'for-you' | 'following' | 'latest' = 'latest', page = 1, limit = 20) => {
    const { data: { user } } = await supabase.auth.getUser();
    const offset = (page - 1) * limit;

    let query = (supabase as any)
      .from('posts')
      .select(`
        *,
        profiles (*),
        post_media (*),
        likes (user_id),
        reposts (user_id),
        bookmarks (user_id)
      `)
      .eq('is_repost', false)
      .is('reply_to_id', null)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (filter === 'following' && user) {
      const { data: follows } = await (supabase as any)
        .from('follows')
        .select('following_id')
        .eq('follower_id', user.id);
      const followingIds = follows?.map((f: any) => f.following_id) || [];
      if (followingIds.length > 0) {
        query = query.in('user_id', followingIds);
      } else {
        return { data: [], error: null };
      }
    }

    const { data, error } = await query;
    return { data: await attachPostMeta(data), error };
  },
  getPostsByUser: async (userId: string, limit = 50) => {
    const { data, error } = await (supabase as any)
      .from('posts')
      .select(`
        *,
        profiles (*),
        post_media (*),
        likes (user_id),
        reposts (user_id),
        bookmarks (user_id)
      `)
      .eq('user_id', userId)
      .eq('is_repost', false)
      .is('reply_to_id', null)
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: await attachPostMeta(data), error };
  },
  getRepliesByUser: async (userId: string, limit = 50) => {
    const { data, error } = await (supabase as any)
      .from('posts')
      .select(`
        *,
        profiles (*),
        post_media (*),
        likes (user_id),
        reposts (user_id),
        bookmarks (user_id)
      `)
      .eq('user_id', userId)
      .not('reply_to_id', 'is', null)
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: await attachPostMeta(data), error };
  },
  getReplies: async (rootPostId: string, limit = 200) => {
    const { data, error } = await (supabase as any)
      .from('posts')
      .select(`
        *,
        profiles (*),
        post_media (*),
        likes (user_id),
        reposts (user_id),
        bookmarks (user_id)
      `)
      .eq('root_post_id', rootPostId)
      .not('reply_to_id', 'is', null)
      .order('created_at', { ascending: true })
      .limit(limit);
    return { data: await attachPostMeta(data), error };
  },
  getPostById: async (postId: string) => {
    const { data, error } = await (supabase as any)
      .from('posts')
      .select(`
        *,
        profiles (*),
        post_media (*),
        likes (*),
        reposts (*),
        bookmarks (*)
      `)
      .eq('id', postId)
      .single();
    return { data: data ? (await attachPostMeta([data]))[0] : data, error };
  },
  likePost: async (postId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { error: new Error('Not authenticated') };
    const { error } = await (supabase as any).from('likes').insert({ user_id: user.id, post_id: postId });
    return { error };
  },
  unlikePost: async (postId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { error: new Error('Not authenticated') };
    const { error } = await (supabase as any)
      .from('likes')
      .delete()
      .eq('user_id', user.id)
      .eq('post_id', postId);
    return { error };
  },
  deletePost: async (postId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { error: new Error('Not authenticated') };
    const { error } = await (supabase as any).from('posts').delete().eq('id', postId).eq('user_id', user.id);
    return { error };
  },
  bookmarkPost: async (postId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { error: new Error('Not authenticated') };
    const { error } = await (supabase as any).from('bookmarks').insert({ user_id: user.id, post_id: postId });
    return { error };
  },
  unbookmarkPost: async (postId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { error: new Error('Not authenticated') };
    const { error } = await (supabase as any)
      .from('bookmarks')
      .delete()
      .eq('user_id', user.id)
      .eq('post_id', postId);
    return { error };
  },
  repostPost: async (postId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { error: new Error('Not authenticated') };
    const { error } = await (supabase as any)
      .from('reposts')
      .insert({ user_id: user.id, post_id: postId });
    return { error };
  },
  unrepostPost: async (postId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { error: new Error('Not authenticated') };
    const { error } = await (supabase as any)
      .from('reposts')
      .delete()
      .eq('user_id', user.id)
      .eq('post_id', postId);
    return { error };
  },
  quotePost: async (postId: string, caption?: string | null) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { error: new Error('Not authenticated') };
    const { data, error } = await (supabase as any)
      .from('posts')
      .insert({ user_id: user.id, content: caption || null, quoted_post_id: postId })
      .select(`
        *,
        profiles (*)
      `)
      .single();
    return { data, error };
  },
  getBookmarkedPosts: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: [], error: null };
    const { data, error } = await (supabase as any)
      .from('bookmarks')
      .select(`
        created_at,
        posts (
          *,
          profiles (*),
          post_media (*),
          likes (user_id),
          reposts (user_id),
          bookmarks (user_id)
        )
      `)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    return {
      data: await attachPostMeta((data || []).map((row: any) => row.posts).filter(Boolean)),
      error,
    };
  },
  getLikedPosts: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: [], error: null };
    const { data, error } = await (supabase as any)
      .from('likes')
      .select(`
        created_at,
        posts (
          *,
          profiles (*),
          post_media (*),
          likes (user_id),
          reposts (user_id),
          bookmarks (user_id)
        )
      `)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    return {
      data: await attachPostMeta((data || []).map((row: any) => row.posts).filter(Boolean)),
      error,
    };
  },
  getRepostedPosts: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: [], error: null };
    const { data, error } = await (supabase as any)
      .from('reposts')
      .select(`
        created_at,
        posts (
          *,
          profiles (*),
          post_media (*),
          likes (user_id),
          reposts (user_id),
          bookmarks (user_id)
        )
      `)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    return {
      data: await attachPostMeta((data || []).map((row: any) => row.posts).filter(Boolean)),
      error,
    };
  },
};

export const followService = {
  follow: async (targetUserId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { error: new Error('Not authenticated') };
    const { error } = await (supabase as any).from('follows').insert({ follower_id: user.id, following_id: targetUserId });
    return { error };
  },
  unfollow: async (targetUserId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { error: new Error('Not authenticated') };
    const { error } = await (supabase as any)
      .from('follows')
      .delete()
      .eq('follower_id', user.id)
      .eq('following_id', targetUserId);
    return { error };
  },
  isFollowing: async (targetUserId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: false, error: null };
    const { data } = await (supabase as any)
      .from('follows')
      .select('id')
      .eq('follower_id', user.id)
      .eq('following_id', targetUserId)
      .maybeSingle();
    return { data: !!data, error: null };
  },
  getCounts: async (userId: string) => {
    const [followers, following] = await Promise.all([
      (supabase as any)
        .from('follows')
        .select('*', { count: 'exact', head: true })
        .eq('following_id', userId),
      (supabase as any)
        .from('follows')
        .select('*', { count: 'exact', head: true })
        .eq('follower_id', userId),
    ]);
    return {
      data: { followers: followers.count || 0, following: following.count || 0 },
      error: followers.error || following.error || null,
    };
  },
};

export const notificationService = {
  getNotifications: async (page = 1, limit = 20) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: [], error: null };
    const offset = (page - 1) * limit;
    const { data, error } = await (supabase as any)
      .from('notifications')
      .select(`
        *,
        profiles!actor_id (*)
      `)
      .eq('recipient_id', user.id)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);
    return { data, error };
  },
  markAsRead: async (notificationId: string) => {
    const { error } = await (supabase as any)
      .from('notifications')
      .update({ read: true })
      .eq('id', notificationId);
    return { error };
  },
  markAllAsRead: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { error: new Error('Not authenticated') };
    const { error } = await (supabase as any)
      .from('notifications')
      .update({ read: true })
      .eq('recipient_id', user.id)
      .eq('read', false);
    return { error };
  },
  getUnreadCount: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: 0, error: null };
    const { count, error } = await (supabase as any)
      .from('notifications')
      .select('*', { head: true, count: 'exact' })
      .eq('recipient_id', user.id)
      .eq('read', false);
    return { data: count || 0, error };
  },
};

export const messageService = {
  getConversations: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: [], error: null };
    const { data: members } = await (supabase as any)
      .from('conversation_members')
      .select('conversation_id')
      .eq('user_id', user.id);
    if (!members || members.length === 0) return { data: [], error: null };
    const conversationIds = members.map((m: any) => m.conversation_id);
    const { data, error } = await (supabase as any)
      .from('conversations')
      .select(`
        *,
        conversation_members (*, profiles (*)),
        messages (*)
      `)
      .in('id', conversationIds)
      .order('updated_at', { ascending: false });
    return { data, error };
  },
  getMessages: async (conversationId: string, page = 1, limit = 50) => {
    const offset = (page - 1) * limit;
    const { data, error } = await (supabase as any)
      .from('messages')
      .select('*, profiles (*)')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true })
      .range(offset, offset + limit - 1);
    return { data, error };
  },
  sendMessage: async (conversationId: string, content: string, mediaUrl?: string, mediaType?: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: null, error: new Error('Not authenticated') };
    const { data, error } = await (supabase as any)
      .from('messages')
      .insert({ conversation_id: conversationId, sender_id: user.id, content, media_url: mediaUrl, media_type: mediaType })
      .select('*, profiles (*)')
      .single();
    return { data, error };
  },
  getConversation: async (conversationId: string) => {
    const { data, error } = await (supabase as any)
      .from('conversations')
      .select(`
        *,
        conversation_members (*, profiles (*)),
        messages (*)
      `)
      .eq('id', conversationId)
      .single();
    return { data, error };
  },
  /**
   * Opens the 1:1 thread with another user, creating it on first contact.
   * Backed by the `get_or_create_direct_conversation` SQL function, which adds
   * both members atomically (RLS only lets a user add themselves).
   */
  getOrCreateConversation: async (otherUserId: string) => {
    const { data, error } = await (supabase as any).rpc('get_or_create_direct_conversation', {
      other_user_id: otherUserId,
    });
    return { data: (data as string | null) ?? null, error };
  },
  getUnreadCounts: async () => {
    const { data, error } = await (supabase as any).rpc('get_unread_message_counts');
    return { data: (data || []) as { conversation_id: string; unread_count: number }[], error };
  },
  markConversationRead: async (conversationId: string) => {
    const { error } = await (supabase as any).rpc('mark_conversation_read', {
      target_conversation_id: conversationId,
    });
    return { error };
  },
  sendPushNotification: async (
    targetUserId: string,
    title: string,
    body: string,
    data?: Record<string, any>,
  ) => {
    const { error } = await (supabase as any)
      .functions.invoke("send-push-notification", {
        body: {
          target_user_id: targetUserId,
          title,
          body,
          data: data || {},
        },
      });
    return { error };
  },
};

export const searchService = {
  searchUsers: async (query: string) => {
    const { data, error } = await (supabase as any)
      .from('profiles')
      .select('*')
      .ilike('username', `%${query}%`)
      .limit(20);
    return { data, error };
  },
  searchPosts: async (query: string) => {
    const { data, error } = await (supabase as any)
      .from('posts')
      .select(`
        *,
        profiles (*),
        post_media (*)
      `)
      .ilike('content', `%${query}%`)
      .eq('is_repost', false)
      .limit(20);
    return { data, error };
  },
};

export const callService = {
  createCall: async (calleeId: string, kind: 'audio' | 'video') => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: null, error: new Error('Not authenticated') };
    const { data, error } = await (supabase as any)
      .from('calls')
      .insert({ caller_id: user.id, callee_id: calleeId, kind, status: 'ringing' })
      .select('*')
      .single();
    return { data, error };
  },
  updateStatus: async (
    callId: string,
    status: 'accepted' | 'declined' | 'ended' | 'missed',
  ) => {
    const patch: Record<string, any> = { status };
    if (status === 'accepted') patch.answered_at = new Date().toISOString();
    if (status === 'declined' || status === 'ended' || status === 'missed') {
      patch.ended_at = new Date().toISOString();
    }
    const { error } = await (supabase as any)
      .from('calls')
      .update(patch)
      .eq('id', callId);
    return { error };
  },
};

